/**
 * Resource TYPOLOGY metadata for the Appsmith-style segmented resource picker
 * (ADR-0044 U-track). A page query carries a `source_kind`
 * (`table`/`dataset`/`http`/`js`/`static`); the picker groups the declared
 * queries by kind with a per-kind icon + Italian label so the operator browses
 * "Tabelle / Dataset / HTTP / JS / Statici" instead of one flat list (the owner's
 * Appsmith "Connect to query" reference). Pure data + a grouping helper — no React
 * — so it's unit-testable and reused by every picker (the `data` field + the
 * `run_query` action).
 */

/** The query source kinds the picker segments by (mirrors `PageQuery.source_kind`). */
export type ResourceKind =
  | "query"
  | "table"
  | "dataset"
  | "http"
  | "js"
  | "logic"
  | "static";

/** Display order + Italian label + a lucide icon NAME (resolved by the component,
 *  so this file pulls in no React/icon imports). `other` catches an unknown kind.
 *  `query` = a reference to a named app Query (ADR-0048 convergence). */
export const RESOURCE_KIND_META: Record<
  ResourceKind | "other",
  { label: string; icon: string; order: number }
> = {
  query: { label: "Query app", icon: "Server", order: 0 },
  table: { label: "Tabelle", icon: "Table2", order: 1 },
  dataset: { label: "Dataset", icon: "Boxes", order: 2 },
  http: { label: "HTTP", icon: "Globe", order: 3 },
  js: { label: "Compute", icon: "Braces", order: 4 },
  logic: { label: "Logica", icon: "Braces", order: 5 },
  static: { label: "Statici", icon: "FileJson", order: 6 },
  other: { label: "Altro", icon: "Database", order: 7 },
};

/** A page query as the picker needs it — just its name + (optional) kind. */
export interface ResourceQuery {
  name: string;
  source_kind?: string | null;
}

/** A picker section: a kind (+ its meta) and the queries under it. */
export interface ResourceGroup {
  kind: ResourceKind | "other";
  label: string;
  icon: string;
  queries: ResourceQuery[];
}

/** Normalise a raw `source_kind` to a known kind (unknown / missing → `other`). */
export function resourceKindOf(q: ResourceQuery): ResourceKind | "other" {
  const k = q.source_kind;
  return k && k in RESOURCE_KIND_META && k !== "other" ? (k as ResourceKind) : "other";
}

/**
 * Group queries by typology, optionally filtered by a case-insensitive search over
 * the query NAME. Empty groups are dropped; groups come back in `RESOURCE_KIND_META`
 * order, queries name-sorted within each. Total + never-throws.
 */
export function groupResources(queries: ResourceQuery[], search = ""): ResourceGroup[] {
  const needle = search.trim().toLowerCase();
  const buckets = new Map<ResourceKind | "other", ResourceQuery[]>();
  for (const q of queries) {
    if (!q || typeof q.name !== "string" || !q.name) continue;
    if (needle && !q.name.toLowerCase().includes(needle)) continue;
    const kind = resourceKindOf(q);
    const arr = buckets.get(kind) ?? [];
    arr.push(q);
    buckets.set(kind, arr);
  }
  return [...buckets.entries()]
    .map(([kind, qs]) => ({
      kind,
      label: RESOURCE_KIND_META[kind].label,
      icon: RESOURCE_KIND_META[kind].icon,
      queries: qs.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => RESOURCE_KIND_META[a.kind].order - RESOURCE_KIND_META[b.kind].order);
}
